import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faEdit,
  faTrash,
  faListCheck,
  faSearch,
  faArrowUp,
  faArrowDown,
  faCamera,
  faToggleOn,
  faHashtag,
  faFont,
} from '@fortawesome/free-solid-svg-icons';
import CommonTable from '../../table/ComonTable';
import CommonModal from '../../commonmodal/CommonModal';
import DeleteModal from '../../commonmodal/DeleteModal';
import { roundInspectionApi } from '../roundInspectionApi';

const FIELD_TYPES = [
  { value: 'PASS_FAIL', label: 'Pass / Fail (Yes/No Toggle)', icon: faToggleOn },
  { value: 'NUMERIC', label: 'Numeric Reading (Meter / Gauge)', icon: faHashtag },
  { value: 'TEXT', label: 'Text / Remarks Note', icon: faFont },
  { value: 'PHOTO_MANDATORY', label: 'Mandatory Photo Proof', icon: faCamera },
  { value: 'PHOTO_OPTIONAL', label: 'Optional Photo Proof', icon: faCamera },
];

const ChecklistTemplateBuilder = ({ siteId }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editId, setEditId] = useState(null);

  // Delete Modal State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Form State
  const initialForm = {
    templateName: '',
    category: 'Security',
    description: '',
    isActiveId: 1,
    fields: [
      {
        fieldName: 'checkpoint_status',
        fieldLabel: 'Is the area clean and secure?',
        fieldType: 'PASS_FAIL',
        isMandatory: true,
        orderIndex: 1,
      },
      {
        fieldName: 'checkpoint_photo',
        fieldLabel: 'Take a photo of the location',
        fieldType: 'PHOTO_OPTIONAL',
        isMandatory: false,
        orderIndex: 2,
      },
    ],
  };
  const [formData, setFormData] = useState(initialForm);

  const fetchTemplates = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      const res = await roundInspectionApi.getTemplates(siteId);
      const data = res?.data?.data || res?.data || [];
      if (Array.isArray(data)) {
        setTemplates(data);
      } else {
        setTemplates([]);
      }
    } catch (err) {
      toast.error('Failed to load checklist templates: ' + (err.message || ''));
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Open Add Modal
  const handleOpenAdd = () => {
    setIsEditMode(false);
    setEditId(null);
    setFormData(initialForm);
    setIsModalOpen(true);
  };

  // Open Edit Modal
  const handleOpenEdit = async (template) => {
    const targetId = template.templateId || template.id;
    setIsEditMode(true);
    setEditId(targetId);
    try {
      const res = await roundInspectionApi.getTemplateById(targetId);
      const full = res?.data?.data || res?.data || template;
      setFormData({
        templateName: full.templateName || '',
        category: full.category || 'Security',
        description: full.description || '',
        isActiveId: full.isActiveId !== undefined ? full.isActiveId : (full.isActive ? 1 : 0),
        fields: Array.isArray(full.fields) && full.fields.length > 0
          ? full.fields.map((f, idx) => ({
              id: f.id || f.fieldId || 0,
              fieldId: f.id || f.fieldId || 0,
              fieldName: f.fieldKey || f.fieldName || `field_${idx + 1}`,
              fieldKey: f.fieldKey || f.fieldName || `field_${idx + 1}`,
              fieldLabel: f.fieldLabel || '',
              fieldType: f.fieldType || 'PASS_FAIL',
              isMandatory: Boolean(f.isMandatory),
              orderIndex: f.displayOrder || f.orderIndex || idx + 1,
            }))
          : initialForm.fields,
      });
    } catch {
      setFormData({
        templateName: template.templateName || '',
        category: template.category || 'Security',
        description: template.description || '',
        isActiveId: template.isActiveId !== undefined ? template.isActiveId : (template.isActive ? 1 : 0),
        fields: initialForm.fields,
      });
    }
    setIsModalOpen(true);
  };

  // Add field row
  const handleAddField = () => {
    const nextIdx = formData.fields.length + 1;
    setFormData((prev) => ({
      ...prev,
      fields: [
        ...prev.fields,
        {
          fieldName: `field_${nextIdx}`,
          fieldLabel: '',
          fieldType: 'PASS_FAIL',
          isMandatory: false,
          orderIndex: nextIdx,
        },
      ],
    }));
  };

  // Remove field row
  const handleRemoveField = (index) => {
    setFormData((prev) => {
      const updated = prev.fields.filter((_, idx) => idx !== index);
      return {
        ...prev,
        fields: updated.map((f, i) => ({ ...f, orderIndex: i + 1 })),
      };
    });
  };

  // Move field order
  const handleMoveField = (index, direction) => {
    const newIdx = direction === 'UP' ? index - 1 : index + 1;
    if (newIdx < 0 || newIdx >= formData.fields.length) return;
    setFormData((prev) => {
      const copy = [...prev.fields];
      const temp = copy[index];
      copy[index] = copy[newIdx];
      copy[newIdx] = temp;
      return {
        ...prev,
        fields: copy.map((f, i) => ({ ...f, orderIndex: i + 1 })),
      };
    });
  };

  // Update field property
  const handleFieldChange = (index, key, val) => {
    setFormData((prev) => {
      const copy = [...prev.fields];
      copy[index] = { ...copy[index], [key]: val };
      if (key === 'fieldLabel' && !copy[index].isCustomKey) {
        copy[index].fieldName = val
          .toLowerCase()
          .replace(/[^a-z0-9]/g, '_')
          .substring(0, 30);
      }
      return { ...prev, fields: copy };
    });
  };

  // Save Template
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    if (!formData.templateName.trim()) {
      toast.warn('Please provide a Template Name');
      return;
    }
    if (formData.fields.length === 0) {
      toast.warn('Please add at least one question to the checklist');
      return;
    }
    for (const f of formData.fields) {
      if (!f.fieldLabel.trim()) {
        toast.warn('All checklist questions must have a label');
        return;
      }
    }

    const templateIdVal = isEditMode ? editId : 0;
    const activeVal = parseInt(formData.isActiveId, 10);
    const payload = {
      id: templateIdVal,
      templateId: templateIdVal,
      siteId: Number(siteId),
      templateName: formData.templateName.trim(),
      category: formData.category || 'Security',
      description: formData.description.trim(),
      isActive: activeVal === 1,
      isActiveId: activeVal,
      fields: formData.fields.map((f, idx) => ({
        id: f.id || f.fieldId || 0,
        fieldId: f.id || f.fieldId || 0,
        fieldName: f.fieldName || f.fieldKey || ('field_' + (idx + 1)),
        fieldKey: f.fieldKey || f.fieldName || ('field_' + (idx + 1)),
        fieldLabel: f.fieldLabel.trim(),
        fieldType: f.fieldType || 'PASS_FAIL',
        isMandatory: Boolean(f.isMandatory),
        displayOrder: idx + 1,
        orderIndex: idx + 1,
      })),
    };

    try {
      if (isEditMode) {
        await roundInspectionApi.updateTemplate(editId, payload);
        toast.success('Checklist template updated');
      } else {
        await roundInspectionApi.createTemplate(payload);
        toast.success('Checklist template created');
      }
      setIsModalOpen(false);
      fetchTemplates();
    } catch (err) {
      toast.error('Failed to save template: ' + (err.response?.data?.message || err.message));
    }
  };

  // Confirm Delete
  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      await roundInspectionApi.deleteTemplate(itemToDelete.templateId || itemToDelete.id);
      toast.success('Checklist template deactivated');
      setDeleteModalOpen(false);
      fetchTemplates();
    } catch (err) {
      toast.error('Failed to delete: ' + (err.message || ''));
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredTemplates = templates.filter((t) =>
    (t.templateName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (t.category || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Table Configuration
  const tableHeaders = [
    { label: 'S. NO', key: 'sno', align: 'center' },
    { label: 'TEMPLATE NAME', key: 'templateName', align: 'left' },
    { label: 'CATEGORY', key: 'category', align: 'center' },
    { label: 'QUESTIONS COUNT', key: 'fieldCount', align: 'center' },
    { label: 'STATUS', key: 'status', align: 'center' },
    { label: 'ACTIONS', key: 'actions', align: 'center' },
  ];

  const renderCell = (key, row, header) => {
    switch (key) {
      case 'sno':
        return <span className="font-semibold text-xs text-textsecondary">{filteredTemplates.indexOf(row) + 1}</span>;
      case 'templateName':
        return (
          <div>
            <div className="font-bold text-xs text-textprimary">{row.templateName}</div>
            <div className="text-[11px] text-textsecondary truncate max-w-sm">
              {row.description || 'No description provided.'}
            </div>
          </div>
        );
      case 'category':
        return (
          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-indigo-50 text-indigo-700 dark:bg-indigo-950/30 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-900">
            {row.category || 'General'}
          </span>
        );
      case 'fieldCount':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-slate-100 dark:bg-slate-800 text-xs font-semibold text-textprimary">
            <FontAwesomeIcon icon={faListCheck} className="text-indigo-600 text-[11px]" />
            {row.fieldCount ? `${row.fieldCount} Questions` : 'Dynamic Schema'}
          </span>
        );
      case 'status':
        return row.isActiveId === 1 ? (
          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-50 text-green-700 border border-green-200">
            Active
          </span>
        ) : (
          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 text-gray-500 border border-gray-200">
            Inactive
          </span>
        );
      case 'actions':
        return (
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => handleOpenEdit(row)}
              className="text-blue-600 hover:text-blue-800 transition-colors text-xs"
              title="Edit Template"
            >
              <FontAwesomeIcon icon={faEdit} />
            </button>
            <button
              onClick={() => {
                setItemToDelete(row);
                setDeleteModalOpen(true);
              }}
              className="text-red-500 hover:text-red-700 transition-colors text-xs"
              title="Deactivate Template"
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </div>
        );
      default:
        return row[key] || '-';
    }
  };

  const headerActions = (
    <div className="flex items-center gap-3 flex-wrap">
      {/* Search Input */}
      <div className="relative flex items-center">
        <FontAwesomeIcon icon={faSearch} className="absolute left-3 text-gray-400 text-xs" />
        <input
          type="text"
          placeholder="Search templates..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-8 pr-3 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-bordercolor bg-white dark:bg-cardbg text-textprimary focus:outline-none focus:ring-1 focus:ring-indigo-500 w-48 sm:w-60"
        />
      </div>

      {/* Create Template Button */}
      <button
        onClick={handleOpenAdd}
        className="px-3.5 py-1.5 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
      >
        <FontAwesomeIcon icon={faPlus} />
        Create Template
      </button>
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Table Component */}
      <div className="border border-gray-200 dark:border-bordercolor rounded-xl overflow-hidden shadow-sm">
        <CommonTable
          loading={loading}
          tableHeading="Checklist Templates"
          headers={tableHeaders}
          data={filteredTemplates}
          renderCell={renderCell}
          headerAction={headerActions}
          onEdit={handleOpenEdit}
          onDelete={(row) => {
            setItemToDelete(row);
            setDeleteModalOpen(true);
          }}
        />
      </div>

      {/* Add / Edit Template Modal using CommonModal */}
      <CommonModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={isEditMode ? 'Edit Checklist Template' : 'Create Checklist Template'}
        subTitle="Define question schemas (Pass/Fail, Gauge readings, Photo uploads) for staff when visiting checkpoints."
        footerContent={
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="px-4 py-2 text-xs font-medium rounded-lg border border-gray-300 hover:bg-gray-50 text-gray-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmitForm}
              className="px-4 py-2 text-xs font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 transition-colors shadow-sm"
            >
              {isEditMode ? 'Update Template' : 'Save Template'}
            </button>
          </div>
        }
      >
        <form onSubmit={handleSubmitForm} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-textprimary mb-1 block">
                Template Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="e.g. Daily Security Patrol & Safety Inspection"
                value={formData.templateName}
                onChange={(e) => setFormData({ ...formData, templateName: e.target.value })}
                className="w-full border border-gray-300 dark:border-bordercolor rounded-lg p-2.5 text-xs bg-white dark:bg-cardbg text-textprimary focus:outline-none focus:ring-1 focus:ring-indigo-500"
                required
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-textprimary mb-1 block">Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full border border-gray-300 dark:border-bordercolor rounded-lg p-2.5 text-xs bg-white dark:bg-cardbg text-textprimary focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                <option value="Security">Security</option>
                <option value="Fire & Safety">Fire & Safety</option>
                <option value="Housekeeping">Housekeeping</option>
                <option value="HVAC & Pump">HVAC & Pump Room</option>
                <option value="Electrical">Electrical</option>
                <option value="General Facility">General Facility</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-textprimary mb-1 block">Description</label>
            <textarea
              rows="2"
              placeholder="Brief instructions for field staff regarding this inspection..."
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full border border-gray-300 dark:border-bordercolor rounded-lg p-2.5 text-xs bg-white dark:bg-cardbg text-textprimary focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>

          {/* Dynamic Questions Builder Box */}
          <div className="border border-gray-200 dark:border-bordercolor rounded-xl p-3.5 bg-gray-50/70 dark:bg-dashboardbg/50 space-y-3">
            <div className="flex justify-between items-center">
              <div>
                <span className="text-xs font-bold text-textprimary flex items-center gap-1.5">
                  <FontAwesomeIcon icon={faListCheck} className="text-indigo-600" />
                  Checklist Questions ({formData.fields.length})
                </span>
                <span className="text-[11px] text-textsecondary block">
                  Questions displayed on mobile when staff scans this checkpoint.
                </span>
              </div>
              <button
                type="button"
                onClick={handleAddField}
                className="px-2.5 py-1 text-xs font-semibold rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:bg-indigo-950/40 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 transition-colors flex items-center gap-1"
              >
                <FontAwesomeIcon icon={faPlus} className="text-[10px]" />
                Add Question
              </button>
            </div>

            <div className="space-y-2 max-h-[35vh] overflow-y-auto pr-1">
              {formData.fields.map((field, idx) => (
                <div
                  key={idx}
                  className="bg-white dark:bg-cardbg border border-gray-200 dark:border-bordercolor rounded-xl p-3 shadow-xs space-y-2"
                >
                  <div className="flex items-center gap-2">
                    {/* Order Controls */}
                    <div className="flex flex-col items-center">
                      <button
                        type="button"
                        disabled={idx === 0}
                        onClick={() => handleMoveField(idx, 'UP')}
                        className="text-[10px] text-gray-400 hover:text-gray-600 disabled:opacity-20"
                      >
                        <FontAwesomeIcon icon={faArrowUp} />
                      </button>
                      <span className="text-[10px] font-bold text-textsecondary my-0.5">#{idx + 1}</span>
                      <button
                        type="button"
                        disabled={idx === formData.fields.length - 1}
                        onClick={() => handleMoveField(idx, 'DOWN')}
                        className="text-[10px] text-gray-400 hover:text-gray-600 disabled:opacity-20"
                      >
                        <FontAwesomeIcon icon={faArrowDown} />
                      </button>
                    </div>

                    {/* Question Input */}
                    <div className="flex-1">
                      <input
                        type="text"
                        placeholder="Enter inspection question / item label..."
                        value={field.fieldLabel}
                        onChange={(e) => handleFieldChange(idx, 'fieldLabel', e.target.value)}
                        className="w-full border border-gray-300 dark:border-bordercolor rounded-lg p-2 text-xs bg-white dark:bg-cardbg text-textprimary focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        required
                      />
                    </div>

                    {/* Field Type Select */}
                    <div className="w-48">
                      <select
                        value={field.fieldType}
                        onChange={(e) => handleFieldChange(idx, 'fieldType', e.target.value)}
                        className="w-full border border-gray-300 dark:border-bordercolor rounded-lg p-2 text-xs bg-white dark:bg-cardbg text-textprimary focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      >
                        {FIELD_TYPES.map((ft) => (
                          <option key={ft.value} value={ft.value}>
                            {ft.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Mandatory Toggle */}
                    <div className="flex items-center gap-1.5 px-2">
                      <input
                        type="checkbox"
                        id={`req_${idx}`}
                        checked={field.isMandatory}
                        onChange={(e) => handleFieldChange(idx, 'isMandatory', e.target.checked)}
                        className="h-3.5 w-3.5 rounded border-gray-300 text-indigo-600 cursor-pointer"
                      />
                      <label htmlFor={`req_${idx}`} className="text-[11px] font-medium text-textsecondary cursor-pointer">
                        Req
                      </label>
                    </div>

                    {/* Delete Field */}
                    <button
                      type="button"
                      disabled={formData.fields.length <= 1}
                      onClick={() => handleRemoveField(idx)}
                      className="text-gray-400 hover:text-red-500 disabled:opacity-30 transition-colors p-1"
                    >
                      <FontAwesomeIcon icon={faTrash} className="text-xs" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </form>
      </CommonModal>

      {/* Delete Confirmation Modal */}
      <DeleteModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Deactivate Checklist Template"
        description={`Are you sure you want to deactivate template "${itemToDelete?.templateName}"? Patrol rounds currently using this template will require a reassigned checklist.`}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default ChecklistTemplateBuilder;
